/**
 * RegionFormModal — Sprint 4.2C.
 *
 * Modal para criar/editar fleet_region. Recebe:
 *   - mode: 'create' | 'edit'
 *   - initial: { name?, type?, color?, description?, geometry_geojson? }
 *   - drawnGeometry: opcional GeoJSON capturado do useMapSelection
 *
 * No modo 'create' sem geometry pré-desenhada, mostra hint pra usar
 * a toolbar de seleção (Lasso). Salvar chama createFleetRegion ou
 * updateFleetRegion via api.js.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { createFleetRegion, updateFleetRegion } from "../../lib/api.js";

const TYPES = [
  { id: "bairro",    label: "Bairro" },
  { id: "cidade",    label: "Cidade" },
  { id: "pop_area",  label: "Área POP" },
  { id: "olt_area",  label: "Área OLT" },
  { id: "cto_area",  label: "Área CTO" },
  { id: "custom",    label: "Custom" },
];

const COLORS = ["#2BD9F0", "#a78bfa", "#22c55e", "#FF8A3C", "#FF5063", "#FFB224"];

export default function RegionFormModal({
  open, mode = "create", initial = null, drawnGeometry = null,
  onClose, onSaved,
}) {
  const [name, setName] = useState("");
  const [type, setType] = useState("bairro");
  const [color, setColor] = useState("#2BD9F0");
  const [description, setDescription] = useState("");
  const [geometry, setGeometry] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initial) {
      setName(initial.name || "");
      setType(initial.type || "bairro");
      setColor(initial.color || "#2BD9F0");
      setDescription(initial.description || "");
      setGeometry(initial.geometry_geojson || null);
    } else {
      setName("");
      setType("bairro");
      setColor("#2BD9F0");
      setDescription("");
      setGeometry(drawnGeometry);
    }
    setBusy(false);
    setError(null);
  }, [open, mode, initial, drawnGeometry]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Nome obrigatório.");
      return;
    }
    if (!geometry) {
      setError("Geometria obrigatória. No modo 'criar', desenhe um lasso primeiro (tecla L).");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = { name: name.trim(), type, color, description: description || null, geometry_geojson: geometry };
      if (mode === "edit" && initial?.id) {
        const result = await updateFleetRegion(initial.id, body);
        onSaved?.(result);
      } else {
        const result = await createFleetRegion(body);
        onSaved?.(result);
      }
    } catch (e) {
      setError(e?.message || "Erro ao salvar região");
    } finally {
      setBusy(false);
    }
  };

  // Robustez/simetria (MAP-UX): portaliza p/ document.body como
  // ConfirmCriticalAction/BulkRelayRebootModal — imune a transform/filter futuro
  // em .ipwd-fleet-focus (mapa fullscreen). z-[2000] segue acima do mapa e abaixo
  // da confirmação crítica (z-3000/4000).
  return createPortal(
    <div
      className="fixed inset-0 z-[2000] bg-black/60 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="bg-noc-panel border border-noc-accent/30 rounded shadow-2xl w-full max-w-md p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-noc-accent">
            {mode === "edit" ? "✎ Editar Região" : "+ Nova Região"}
          </h3>
          <button type="button" onClick={onClose} disabled={busy}
            className="text-noc-muted hover:text-noc-text text-lg leading-none disabled:opacity-50"
          >×</button>
        </div>

        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">Nome</span>
          <input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="ex: Centro Cansanção"
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1"
          />
        </label>

        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">Tipo</span>
          <select value={type} onChange={(e) => setType(e.target.value)}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1"
          >
            {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </label>

        <div className="text-xs space-y-1">
          <span className="text-noc-muted">Cor</span>
          <div className="flex gap-2 flex-wrap">
            {COLORS.map((c) => (
              <button
                key={c} type="button"
                onClick={() => setColor(c)}
                className={`w-6 h-6 rounded border-2 ${color === c ? "border-white" : "border-noc-border"}`}
                style={{ background: c }}
                title={c}
              />
            ))}
            <input type="color" value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-6 h-6 rounded bg-noc-bg border border-noc-border cursor-pointer"
              title="Cor custom"
            />
          </div>
        </div>

        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">Descrição (opcional)</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1"
          />
        </label>

        <div className="text-[10px] text-noc-muted bg-noc-bg/50 rounded p-2 border border-noc-border/40">
          <strong>Geometria:</strong>{" "}
          {geometry ? (
            <span className="text-noc-ok">✓ definida ({geometry.type})</span>
          ) : (
            <span className="text-noc-warn">
              ⚠ ausente — feche este modal, desenhe um lasso (tecla <kbd>L</kbd>)
              ou retângulo (<kbd>SHIFT+arrastar</kbd>), depois clique em <strong>+ nova</strong>.
            </span>
          )}
        </div>

        {error && (
          <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} disabled={busy}
            className="text-xs px-3 py-1 rounded border border-noc-border text-noc-muted hover:text-noc-text disabled:opacity-50"
          >Cancelar</button>
          <button type="button" onClick={handleSave} disabled={busy || !geometry}
            className="text-xs px-3 py-1 rounded bg-noc-accent hover:bg-noc-glow text-noc-bg font-semibold disabled:opacity-50"
          >{busy ? "Salvando…" : "Salvar"}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
